// src/pages/Instituicoes/EstudantesVinculadosPage.jsx

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  TextField,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  InputAdornment,
  Grid,
  Divider,
  Alert,
  Card,
  CardContent,
  CardActions
} from '@mui/material';
import {
  Search as SearchIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import useAuth from '../../hooks/useAuth';
import { saveAs } from 'file-saver';

const EstudantesVinculadosPage = () => {
  const { instituicaoId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  
  // Estados para a tabela de estudantes
  const [estudantes, setEstudantes] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalEstudantes, setTotalEstudantes] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // Estados para filtros e busca
  const [busca, setBusca] = useState('');
  const [filtros, setFiltros] = useState({
    status: '',
    modulo: ''
  });
  
  // Estados para módulos
  const [modulos, setModulos] = useState([]);
  const [loadingModulos, setLoadingModulos] = useState(false);
  
  // Estados para diálogos
  const [dialogModulos, setDialogModulos] = useState(false);
  const [dialogDesativar, setDialogDesativar] = useState(false);
  const [estudanteSelecionado, setEstudanteSelecionado] = useState(null);
  const [modulosSelecionados, setModulosSelecionados] = useState([]);
  const [loadingAction, setLoadingAction] = useState(false);
  
  // Efeito para carregar estudantes
  useEffect(() => {
    const fetchEstudantes = async () => {
      try {
        setLoading(true);
        
        // Construir URL com filtros
        let url = `/vinculos/instituicao/${instituicaoId}?`;
        if (filtros.status) url += `status=${filtros.status}&`;
        if (filtros.modulo) url += `modulo=${filtros.modulo}&`;
        
        // Adicionar paginação
        url += `page=${page + 1}&limit=${rowsPerPage}`;
        
        const response = await api.get(url);
        
        setEstudantes(response.data.vinculos || response.data);
        setTotalEstudantes(response.data.total || response.data.length);
      } catch (error) {
        console.error('Erro ao carregar estudantes vinculados:', error);
        enqueueSnackbar('Erro ao carregar estudantes vinculados', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };
    
    if (instituicaoId) {
      fetchEstudantes();
    }
  }, [instituicaoId, page, rowsPerPage, filtros, enqueueSnackbar]);
  
  // Efeito para carregar módulos
  useEffect(() => {
    const fetchModulos = async () => {
      try {
        setLoadingModulos(true);
        const response = await api.get(`/modulos/instituicao/${instituicaoId}`);
        
        // Filtrar apenas os módulos ativos
        const modulosAtivos = response.data.filter(m => m.instituicao_ativo);
        setModulos(modulosAtivos);
      } catch (error) {
        console.error('Erro ao carregar módulos:', error);
        enqueueSnackbar('Erro ao carregar módulos disponíveis', { variant: 'error' });
      } finally {
        setLoadingModulos(false);
      }
    };
    
    if (instituicaoId) {
      fetchModulos();
    }
  }, [instituicaoId, enqueueSnackbar]);
  
  // Handler para mudar página
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  // Handler para mudar linhas por página
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Handler para mudança nos filtros
  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltros(prev => ({ ...prev, [name]: value }));
    setPage(0);
  };
  
  // Handler para busca
  const handleBuscaChange = (e) => {
    setBusca(e.target.value);
  };
  
  const handleBuscaSubmit = (e) => {
    e.preventDefault();
    // Implementar a lógica de busca
  };
  
  // Função para exportar dados como CSV
  const exportarDados = () => {
    // Preparar dados para CSV
    const header = 'Nome,Email,Username,Status,Data Vinculação,Tipo Vínculo\n';
    const rows = estudantes.map(vinculo => {
      const estudante = vinculo.estudante?.usuario || {};
      const dataVinculacao = vinculo.dataVinculacao ? 
        format(new Date(vinculo.dataVinculacao), 'dd/MM/yyyy', { locale: ptBR }) : '';
      
      return `${estudante.nome || ''},${estudante.email || ''},${estudante.username || ''},${vinculo.status},${dataVinculacao},${vinculo.tipoVinculo}`;
    }).join('\n');
    
    const csv = header + rows;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, `estudantes_vinculados_${instituicaoId}_${new Date().toISOString().split('T')[0]}.csv`);
    
    enqueueSnackbar('Dados exportados com sucesso!', { variant: 'success' });
  };
  
  // Abrir diálogo para editar módulos
  const abrirDialogModulos = (estudante) => {
    setEstudanteSelecionado(estudante);
    setModulosSelecionados(estudante.modulosAcesso || []);
    setDialogModulos(true);
  };
  
  // Abrir diálogo para desativar vínculo
  const abrirDialogDesativar = (estudante) => {
    setEstudanteSelecionado(estudante);
    setDialogDesativar(true);
  };
  
  // Função para atualizar módulos de acesso
  const atualizarModulos = async () => {
    if (!estudanteSelecionado) return;
    
    try {
      setLoadingAction(true);
      
      await api.patch(`/vinculos/${estudanteSelecionado.id}/modulos`, {
        modulosAcesso: modulosSelecionados
      });
      
      enqueueSnackbar('Módulos de acesso atualizados com sucesso!', { variant: 'success' });
      
      // Atualizar o estado local
      setEstudantes(prev => 
        prev.map(v => 
          v.id === estudanteSelecionado.id 
            ? { ...v, modulosAcesso: modulosSelecionados } 
            : v
        )
      );
      
      setDialogModulos(false);
    } catch (error) {
      console.error('Erro ao atualizar módulos de acesso:', error);
      enqueueSnackbar(error.response?.data?.mensagem || 'Erro ao atualizar módulos de acesso', { variant: 'error' });
    } finally {
      setLoadingAction(false);
    }
  };
  
  // Função para atualizar status do vínculo
  const atualizarStatus = async (novoStatus) => {
    if (!estudanteSelecionado) return;
    
    try {
      setLoadingAction(true);
      
      await api.patch(`/vinculos/${estudanteSelecionado.id}/status`, {
        status: novoStatus
      });
      
      enqueueSnackbar(`Vínculo ${novoStatus === 'ATIVO' ? 'ativado' : 'desativado'} com sucesso!`, { variant: 'success' });
      
      // Atualizar o estado local
      setEstudantes(prev => 
        prev.map(v => 
          v.id === estudanteSelecionado.id 
            ? { ...v, status: novoStatus } 
            : v
        )
      );
      
      setDialogDesativar(false);
    } catch (error) {
      console.error('Erro ao atualizar status do vínculo:', error);
      enqueueSnackbar(error.response?.data?.mensagem || 'Erro ao atualizar status do vínculo', { variant: 'error' });
    } finally {
      setLoadingAction(false);
    }
  };
  
  // Handler para seleção de módulos
  const handleModulosChange = (event) => {
    const { value } = event.target;
    setModulosSelecionados(value);
  };
  
  // Renderizar chip de status
  const renderStatusChip = (status) => {
    let color;
    
    switch (status) {
      case 'ATIVO':
        color = 'success';
        break;
      case 'INATIVO':
        color = 'error';
        break;
      case 'PENDENTE':
        color = 'warning';
        break;
      default:
        color = 'default';
    }
    
    return <Chip label={status} color={color} size="small" />;
  };
  
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" component="h1" gutterBottom>
        Estudantes Vinculados
      </Typography>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={5}>
            <Box component="form" onSubmit={handleBuscaSubmit}>
              <TextField
                fullWidth
                placeholder="Buscar por nome, email ou username"
                value={busca}
                onChange={handleBuscaChange}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button 
                        type="submit" 
                        variant="text" 
                        size="small" 
                        sx={{ minWidth: 'auto' }}
                      >
                        Buscar
                      </Button>
                    </InputAdornment>
                  )
                }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={filtros.status}
                onChange={handleFiltroChange}
                label="Status"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="ATIVO">Ativo</MenuItem>
                <MenuItem value="INATIVO">Inativo</MenuItem>
                <MenuItem value="PENDENTE">Pendente</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Módulo</InputLabel>
              <Select
                name="modulo"
                value={filtros.modulo}
                onChange={handleFiltroChange}
                label="Módulo"
              >
                <MenuItem value="">Todos os Módulos</MenuItem>
                {modulos.map(modulo => (
                  <MenuItem key={modulo.id} value={modulo.codigo}>
                    {modulo.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              setPage(0);
              // Recarregar estudantes
            }}
            disabled={loading}
          >
            Atualizar
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={exportarDados}
            disabled={estudantes.length === 0 || loading}
          >
            Exportar
          </Button>
          
          <Button
            variant="contained"
            startIcon={<LinkIcon />}
            onClick={() => navigate(`/instituicoes/${instituicaoId}/convites`)}
          >
            Gerenciar Convites
          </Button>
        </Box>
      </Paper>
      
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Data Vinculação</TableCell>
                <TableCell>Tipo de Vínculo</TableCell>
                <TableCell>Módulos</TableCell>
                <TableCell>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress size={30} />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Carregando estudantes...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : estudantes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2">
                      Nenhum estudante vinculado encontrado.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                estudantes.map((vinculo) => {
                  const estudante = vinculo.estudante?.usuario || {};
                  
                  return (
                    <TableRow key={vinculo.id}>
                      <TableCell>{estudante.nome || '-'}</TableCell>
                      <TableCell>{estudante.email || '-'}</TableCell>
                      <TableCell>{estudante.username || '-'}</TableCell>
                      <TableCell>
                        {renderStatusChip(vinculo.status)}
                      </TableCell>
                      <TableCell>
                        {vinculo.dataVinculacao ? 
                          format(new Date(vinculo.dataVinculacao), 'dd/MM/yyyy', { locale: ptBR }) : 
                          '-'}
                      </TableCell>
                      <TableCell>{vinculo.tipoVinculo || 'REGULAR'}</TableCell>
                      <TableCell>
                        {vinculo.modulosAcesso && Array.isArray(vinculo.modulosAcesso) && vinculo.modulosAcesso.length > 0 ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {vinculo.modulosAcesso.map(codigo => {
                              const modulo = modulos.find(m => m.codigo === codigo);
                              return (
                                <Chip 
                                  key={codigo} 
                                  label={modulo ? modulo.nome : codigo} 
                                  size="small" 
                                  variant="outlined"
                                />
                              );
                            })}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Todos os módulos
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Editar Módulos de Acesso">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => abrirDialogModulos(vinculo)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title={vinculo.status === 'ATIVO' ? 'Desativar Vínculo' : 'Ativar Vínculo'}>
                            <IconButton
                              size="small"
                              color={vinculo.status === 'ATIVO' ? 'error' : 'success'}
                              onClick={() => abrirDialogDesativar(vinculo)}
                            >
                              {vinculo.status === 'ATIVO' ? 
                                <LinkOffIcon fontSize="small" /> : 
                                <LinkIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={totalEstudantes}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Itens por página:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
          }
        />
      </Paper>
      
      {/* Diálogo para editar módulos de acesso */}
      <Dialog 
        open={dialogModulos} 
        onClose={() => setDialogModulos(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Editar Módulos de Acesso
        </DialogTitle>
        <DialogContent>
          {estudanteSelecionado && (
            <>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" fontWeight="bold">
                  Estudante: {estudanteSelecionado.estudante?.usuario?.nome || 'Nome não disponível'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Username: {estudanteSelecionado.estudante?.usuario?.username || '-'}
                </Typography>
                {estudanteSelecionado.estudante?.usuario?.email && (
                  <Typography variant="body2" color="text.secondary">
                    Email: {estudanteSelecionado.estudante.usuario.email}
                  </Typography>
                )}
              </Box>
              
              <Divider sx={{ mb: 3 }} />
              
              <Typography variant="body1" gutterBottom>
                Selecione os módulos que este estudante poderá acessar:
              </Typography>
              
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Módulos de Acesso</InputLabel>
                <Select
                  multiple
                  value={modulosSelecionados}
                  onChange={handleModulosChange}
                  input={<OutlinedInput label="Módulos de Acesso" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const modulo = modulos.find(m => m.codigo === value);
                        return (
                          <Chip 
                            key={value} 
                            label={modulo ? modulo.nome : value} 
                            size="small" 
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {loadingModulos ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} /> Carregando módulos...
                    </MenuItem>
                  ) : (
                    modulos.map((modulo) => (
                      <MenuItem key={modulo.id} value={modulo.codigo}>
                        <Checkbox 
                          checked={modulosSelecionados.includes(modulo.codigo)} 
                        />
                        <ListItemText 
                          primary={modulo.nome} 
                          secondary={modulo.descricao}
                        />
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
              
              <Alert severity="info" sx={{ mt: 3 }}>
                <Typography variant="body2">
                  Deixe a seleção vazia para conceder acesso a <strong>todos os módulos</strong> disponíveis para a instituição.
                </Typography>
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogModulos(false)} disabled={loadingAction}>
            Cancelar
          </Button>
          <Button 
            onClick={atualizarModulos} 
            variant="contained" 
            disabled={loadingAction}
            startIcon={loadingAction ? <CircularProgress size={20} /> : null}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Diálogo para ativar/desativar vínculo */}
      <Dialog 
        open={dialogDesativar} 
        onClose={() => setDialogDesativar(false)}
      >
        <DialogTitle>
          {estudanteSelecionado?.status === 'ATIVO' 
            ? 'Desativar Vínculo' 
            : 'Ativar Vínculo'}
        </DialogTitle>
        <DialogContent>
          {estudanteSelecionado && (
            <Typography variant="body1">
              Tem certeza que deseja {estudanteSelecionado.status === 'ATIVO' ? 'desativar' : 'ativar'} o vínculo com o estudante <strong>{estudanteSelecionado.estudante?.usuario?.nome || 'Nome não disponível'}</strong>?
            </Typography>
          )}
          
          {estudanteSelecionado?.status === 'ATIVO' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Ao desativar o vínculo, o estudante perderá acesso a todos os módulos da instituição.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogDesativar(false)} disabled={loadingAction}>
            Cancelar
          </Button>
          <Button 
            onClick={() => atualizarStatus(estudanteSelecionado?.status === 'ATIVO' ? 'INATIVO' : 'ATIVO')} 
            variant="contained" 
            color={estudanteSelecionado?.status === 'ATIVO' ? 'error' : 'success'}
            disabled={loadingAction}
            startIcon={loadingAction ? <CircularProgress size={20} /> : null}
          >
            {estudanteSelecionado?.status === 'ATIVO' ? 'Desativar' : 'Ativar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EstudantesVinculadosPage;